import { isEqual } from 'lodash';

import type {
  RecipeIngredient,
  ActivityIngredient,
  HotelIngredient,
} from '@apps/main/modules/recipe/ingredients';
import { getImageUrl } from '@apps/main/modules/api/hotels';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';
import { money } from '@apps/main/modules/helpers/formatting';

import { isFeatureToggleActive } from '@libs/feature-toggle';
import { isAvailableActivityIngredientWithStaticInfoResponse } from '@libs/search-service-smart-search';
import type {
  HotelIngredientResponseWithStaticInfo,
  ActivityIngredientResponseWithStaticInfo,
  OwnStayIngredientResponseWithStaticInfo,
  AvailableActivityIngredientResponseWithStaticInfo,
  IngredientId,
  SelectedTicketInfo,
  ActivitySuggestionWithStaticInfo,
  ActivityStaticInfo,
} from '@libs/trip-planner-interfaces';
import { SemboDate, DateFormat } from '@libs/sembo-dates';

import type {
  ActivityProductCardContent,
  ProductInfo,
  ItineraryActivitySegment,
  GenericActivityBannerData,
  ItineraryUpsellSegment,
  SuggestedActivitiesBannerData,
  StaticSuggestedActivitiesBannerData,
} from '../../models';
import {
  ItinerarySegmentType,
  ProductCardType,
  UnavailabilityReason,
} from '../../models';
import { getChangeButtonText } from '../tripOverviewStoreLogic';

import { REQUSTED_IMAGE_SIZE } from './tripOverviewHotelRelatedSegmentsLogic';

const { t } = i18n.global;

export function getActivitySegments(
  stayIngredient:
    | HotelIngredientResponseWithStaticInfo
    | OwnStayIngredientResponseWithStaticInfo,
  getIngredient: (id: string) => RecipeIngredient,
  ingredientIndex: number,
  selectedDate?: Date
): ItineraryActivitySegment[] {
  return stayIngredient.subIngredientResponseWithStaticInfo.reduce(
    (
      acc: ItineraryActivitySegment[],
      activityIngredient: ActivityIngredientResponseWithStaticInfo
    ) => {
      const activityIngredientWithTickets = getIngredient(
        activityIngredient.ingredientId
      ) as ActivityIngredient;

      if (
        isAvailableActivityIngredientWithStaticInfoResponse(
          activityIngredient
        ) &&
        !isFeatureToggleActive('show-availability-errors')
      ) {
        acc.push(
          getTripOverviewActivitySegment(
            stayIngredient.ingredientId,
            activityIngredient,
            activityIngredientWithTickets,
            ingredientIndex
          )
        );
      } else {
        acc.push(
          getTripOverviewUnavailableActivitySegment(
            stayIngredient.ingredientId,
            activityIngredientWithTickets.ingredientId,
            activityIngredient.date,
            ingredientIndex,
            selectedDate
          )
        );
      }

      return acc;
    },
    []
  );
}

export function getTripOverviewActivitySegment(
  parentIngredientId: IngredientId,
  ingredient: AvailableActivityIngredientResponseWithStaticInfo,
  activityIngredientWithTickets: ActivityIngredient,
  ingredientIndex: number
): ItineraryActivitySegment {
  return {
    segmentType: ItinerarySegmentType.Activity,
    productCardData: {
      imgSrcs: ingredient.staticInfo.images.map((image) =>
        getImageUrl(image, undefined, REQUSTED_IMAGE_SIZE, REQUSTED_IMAGE_SIZE)
      ),
      productCardContent: getActivityProductCardContent(
        ingredient,
        activityIngredientWithTickets
      ),
    },
    context: {
      hotelCode: ingredient.staticInfo.hotelCode,
      accommodationType: ingredient.staticInfo.typeOfHotel,
      parentIngredientId,
      ingredientId: ingredient.ingredientId,
      ingredientIndex,
    },
    date: new SemboDate(ingredient.date).format(DateFormat.DateWithFullMonth),
    isAvailable: true,
  };
}

export function getActivityProductCardContent(
  ingredient: AvailableActivityIngredientResponseWithStaticInfo,
  activityIngredientWithTickets: ActivityIngredient
): ActivityProductCardContent {
  return {
    header: ingredient.staticInfo.name,
    subheader: '',
    rating: {
      stars: ingredient.staticInfo.semboSunRating,
    },
    cardDetails: getActivityProductCardDetails(activityIngredientWithTickets),
    changeButtonText: getChangeButtonText(ProductCardType.Activity),
  };
}

export function getActivityProductCardDetails(
  activityIngredientWithTickets: ActivityIngredient
): ProductInfo[] {
  return activityIngredientWithTickets.tickets.map(
    (ticket: SelectedTicketInfo) => ({
      label: `${ticket.quantity} ${t('planner.tickets', ticket.quantity)}`,
      description: getActivityProductCardDetailsDescription(ticket),
    })
  );
}

export function getActivityProductCardDetailsDescription(
  ticket: SelectedTicketInfo
): string {
  const descriptions: string[] = [];

  if (ticket.rate.value) {
    descriptions.push(ticket.rate.value);
  }

  if (ticket.timeSlot?.value) {
    descriptions.push(ticket.timeSlot?.value);
  }

  return descriptions.join(', ');
}

export function getTripOverviewUnavailableActivitySegment(
  parentIngredientId: IngredientId,
  ingredientId: IngredientId,
  activityDate: Date,
  ingredientIndex: number,
  selectedDate?: Date
): ItineraryActivitySegment {
  return {
    segmentType: ItinerarySegmentType.Activity,
    isAvailable: false,
    productCardData: null,
    context: {
      parentIngredientId,
      ingredientId,
      ingredientIndex,
    },
    date: new SemboDate(activityDate).format(DateFormat.DateWithFullMonth),
    reasons: getUnavailableActivitySegmentReasons(selectedDate),
  };
}

export function getUnavailableActivitySegmentReasons(
  selectedDate?: Date
): UnavailabilityReason[] {
  const reasons: UnavailabilityReason[] = [];

  if (selectedDate) {
    reasons.push(UnavailabilityReason.SelectedDate);
  }

  reasons.push(UnavailabilityReason.SelectedProduct);

  return reasons;
}

export function getUnavailableActivityMessage(
  reasons: UnavailabilityReason[]
): string {
  if (isEqual(reasons, [UnavailabilityReason.SelectedProduct])) {
    return t('planner.no_availability.messages.activity_selected');
  }

  if (
    reasons.includes(UnavailabilityReason.SelectedDate) &&
    reasons.includes(UnavailabilityReason.SelectedProduct)
  ) {
    return t(
      'planner.no_availability.messages.activity_selected_date_selected'
    );
  }

  return t('notification.no_available_with_current_filters');
}

export function getUppsellSegment(
  ingredient: HotelIngredient | OwnStayIngredient,
  areThereActivitiesInDestination: boolean,
  activitySuggestions: ActivitySuggestionWithStaticInfo[],
  staticActivitySuggestions: ActivityStaticInfo[]
): ItineraryUpsellSegment | null {
  const hasIngredientActivity = ingredient.subIngredients.length > 0;

  const suggestedActivitiesBanner: SuggestedActivitiesBannerData = {
    isVisible: activitySuggestions.length > 0 && !hasIngredientActivity,
    suggestedActivities: activitySuggestions.map(
      ({ images, name, price, code }) => ({
        price: price ? money(price) : undefined,
        name,
        imageUrl: getImageUrl(images[0]),
        productCode: code,
      })
    ),
    destination: ingredient.displayText,
  };

  const staticSuggestedActivitiesBanner: StaticSuggestedActivitiesBannerData = {
    isVisible: staticActivitySuggestions.length > 0 && !hasIngredientActivity,
    suggestedActivities: staticActivitySuggestions.map(
      ({ hotelCode, images, name }) => ({
        imageUrl: getImageUrl(images[0]),
        name,
        productCode: hotelCode,
      })
    ),
    destination: ingredient.displayText.split(',')[0] ?? ingredient.displayText,
  };

  const genericActivityBanner: GenericActivityBannerData = {
    isVisible: hasIngredientActivity && areThereActivitiesInDestination,
  };

  if (
    !genericActivityBanner.isVisible &&
    !suggestedActivitiesBanner.isVisible &&
    !staticSuggestedActivitiesBanner.isVisible
  ) {
    return null;
  }

  return {
    segmentType: ItinerarySegmentType.Upsell,
    productCardData: {
      genericActivityBanner,
      suggestedActivitiesBanner,
      staticSuggestedActivitiesBanner,
      ingredientId: ingredient.ingredientId,
    },
  };
}
